import { Box, ButtonBase, Chip, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import SearchIcon from '@/icons/Search'
import DocsSearchDialog from './DocsSearchDialog'

/**
 * The docs-search affordance: a fake input in the navbar that opens the
 * real search dialog, plus the Cmd+K / Ctrl+K shortcut.
 *
 * A button rather than a live `<input>` in the bar, because the dialog owns
 * the query state and focus — a real input here would need its value
 * handed across on open and would compete for focus with the dialog's own
 * field. This is the same shape most docs sites use, and it keeps the
 * search index out of the bundle until someone actually asks for it (the
 * dialog reads `useAuth()` itself, so this button takes no props).
 */

// `navigator.platform` is deprecated, but the replacement
// (`navigator.userAgentData`) isn't supported everywhere either, so this
// falls back through both — it's only ever used to label the shortcut hint,
// never to branch behaviour, so "occasionally wrong" is an acceptable cost.
function resolveShortcutLabel() {
    if (typeof navigator === 'undefined') return 'Ctrl K'
    const platform =
        navigator.userAgentData?.platform ?? navigator.platform ?? ''
    return /Mac|iPhone|iPad|iPod/.test(platform) ? '⌘ K' : 'Ctrl K'
}

export default function DocsSearchButton() {
    const [open, setOpen] = useState(false)
    const [shortcutLabel, setShortcutLabel] = useState('Ctrl K')

    useEffect(() => {
        setShortcutLabel(resolveShortcutLabel())
    }, [])

    useEffect(() => {
        const onKeyDown = (event) => {
            if (
                event.key.toLowerCase() === 'k' &&
                (event.metaKey || event.ctrlKey)
            ) {
                event.preventDefault()
                setOpen((previous) => !previous)
            }
        }
        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [])

    return (
        <>
            <ButtonBase
                onClick={() => setOpen(true)}
                aria-label="Search the documentation"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1,
                    border: 1,
                    borderColor: 'divider',
                    color: 'text.secondary',
                    minWidth: { xs: 40, sm: 240 },
                    justifyContent: 'flex-start',
                    '&:hover': { borderColor: 'text.disabled' }
                }}
            >
                <SearchIcon fontSize="small" />
                <Typography
                    variant="body2"
                    sx={{ display: { xs: 'none', sm: 'block' } }}
                >
                    Search docs
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Chip
                    label={shortcutLabel}
                    size="small"
                    variant="outlined"
                    sx={{
                        height: 20,
                        fontSize: '0.6875rem',
                        display: { xs: 'none', sm: 'flex' }
                    }}
                />
            </ButtonBase>

            <DocsSearchDialog open={open} onClose={() => setOpen(false)} />
        </>
    )
}
